/**
 * Avatar — circular user image with gradient-initials fallback.
 *
 * Owner red line: NEVER a grey "U" or grey block placeholder. The fallback is
 * always initials over the brand gradient.
 *
 * Sizes: 20 / 28 / 36 / 44 / 64. Optional status dot (bottom-right).
 */

import { useState, type CSSProperties } from "react";

import { useAuthImageUrl } from "@/components/viewer/AuthImage";

/** An API path (children's photos) rather than a plain image URL. */
function isApiPath(src: string | null | undefined): src is string {
  return typeof src === "string" && src.startsWith("/") && !src.startsWith("/media/");
}

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";
export type AvatarStatus = "online" | "away" | "busy" | "offline";

export interface AvatarProps {
  src?: string | null;
  name?: string;
  size?: AvatarSize;
  status?: AvatarStatus;
  className?: string;
  style?: CSSProperties;
}

const SIZE_PX: Record<AvatarSize, number> = {
  xs: 20,
  sm: 28,
  md: 36,
  lg: 44,
  xl: 64,
};

const STATUS_COLOR: Record<AvatarStatus, string> = {
  online: "var(--color-success)",
  away: "var(--color-warning)",
  busy: "var(--color-danger)",
  offline: "var(--color-muted)",
};

function initialsOf(name?: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join("") || "?";
}

export function Avatar({
  src: rawSrc,
  name,
  size = "md",
  status,
  className,
  style,
}: AvatarProps) {
  const px = SIZE_PX[size];
  const fetched = useAuthImageUrl(isApiPath(rawSrc) ? rawSrc : null);
  const src = isApiPath(rawSrc) ? fetched.src : rawSrc;
  const [errored, setErrored] = useState(false);
  const showImage = Boolean(src) && !errored;
  const initials = initialsOf(name);

  return (
    <span
      role="img"
      aria-label={name ?? "User avatar"}
      className={className}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: px,
        height: px,
        borderRadius: "var(--radius-pill)",
        overflow: "hidden",
        background: "var(--brand-gradient)",
        color: "var(--color-on-accent)",
        font: `600 ${Math.round(px * 0.38)}px/1 var(--font-sans)`,
        letterSpacing: 0,
        flexShrink: 0,
        ...style,
      }}
    >
      {showImage && src ? (
        <img
          src={src}
          alt={name ?? ""}
          onError={() => setErrored(true)}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <span>{initials}</span>
      )}
      {status ? (
        <span
          aria-hidden
          style={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: Math.max(8, Math.round(px * 0.24)),
            height: Math.max(8, Math.round(px * 0.24)),
            borderRadius: "var(--radius-pill)",
            background: STATUS_COLOR[status],
            boxShadow: "0 0 0 2px var(--color-surface)",
          }}
        />
      ) : null}
    </span>
  );
}
